import {
  type DescribeLabels,
  IMPACT_LABEL,
  SCORE_IMPACTS,
  type ScoreCriterion,
  type ScoreEntity,
  type ScoreImpact,
  type WhereGroup,
  type WhereRelation,
  type WhereRule,
  criterionLabel,
  describeRule,
  isWhereGroup,
  operatorsFor,
  scoreCriterionSchema,
  whereField,
  whereFieldName,
  whereRules,
  whereValueText,
} from '@parcel/filters';

export { IMPACT_LABEL, SCORE_IMPACTS };
export type { ScoreImpact };

export interface EditorState {
  criteria: ScoreCriterion[];
  dirty: boolean;
  /** Criteria shown as Custom condition cards whatever their shape: picked as custom, or loaded as one. */
  custom?: ReadonlySet<string>;
}

export type NewCriterion = Omit<ScoreCriterion, 'id'> & { id?: string };

/** Which card a criterion sits on: one field, one linked record ("Works on projects"), or a custom condition. */
export type CardKind =
  | { type: 'field'; key: string }
  | { type: 'relation'; relation: WhereRelation; negated: boolean }
  | { type: 'custom'; id: string };

/** The rail entries and card titles for each relation, with and without (a negated group). */
export const RELATION_CARD_TITLE: Record<
  ScoreEntity,
  Partial<Record<WhereRelation, { has: string; none?: string }>>
> = {
  account: {
    project: { has: 'Works on projects', none: 'Works on no projects' },
    contact: { has: 'Has contacts', none: 'Has no contacts' },
    signal: { has: 'Has signals', none: 'Has no signals' },
  },
  project: {
    firm: { has: 'Firms on it', none: 'No firm on it' },
    contact: { has: 'Contacts on it', none: 'No contacts on it' },
    signal: { has: 'Has signals', none: 'Has no signals' },
  },
  contact: {
    // Their firm always exists, so there is no negated card.
    firm: { has: 'Their firm' },
    project: { has: 'Works on projects', none: 'Works on no projects' },
  },
};

/** A condition whose field is not picked yet: "~" in a custom condition, "project." on Works on projects. */
export const isUnpicked = (key: string | undefined): boolean =>
  !key || key === '~' || key.endsWith('.');

/** The relation a field sits on, picked or still a "project." placeholder. */
const relationOf = (entity: ScoreEntity, key: string): WhereRelation | undefined =>
  whereField(entity, key)?.relation ??
  (key.endsWith('.') ? (key.slice(0, -1) as WhereRelation) : undefined);

const onlyRules = (where: WhereGroup): WhereRule[] | null =>
  where.rules.every((node) => !isWhereGroup(node)) ? (where.rules as WhereRule[]) : null;

/** One rule on a base field is a field card; an "all of" group on one relation is that relation's card; else custom. */
export function cardOf(
  entity: ScoreEntity,
  criterion: ScoreCriterion,
  custom?: ReadonlySet<string>,
): CardKind {
  if (custom?.has(criterion.id)) return { type: 'custom', id: criterion.id };
  const rules = onlyRules(criterion.where);
  if (rules && criterion.where.combinator === 'and' && rules.length > 0) {
    const fields = rules.map((r) => whereField(entity, r.field));
    const relations = new Set(rules.map((r) => relationOf(entity, r.field)));
    const [relation] = [...relations];
    if (!criterion.where.not && rules.length === 1 && fields[0] && !relation) {
      return { type: 'field', key: rules[0]?.field as string };
    }
    const title = relation ? RELATION_CARD_TITLE[entity][relation] : undefined;
    const negated = criterion.where.not === true;
    if (relations.size === 1 && relation && title && (!negated || title.none)) {
      return { type: 'relation', relation, negated };
    }
  }
  return { type: 'custom', id: criterion.id };
}

export const cardKey = (card: CardKind): string =>
  card.type === 'field'
    ? `field:${card.key}`
    : card.type === 'relation'
      ? `relation:${card.relation}:${card.negated ? 'none' : 'has'}`
      : `custom:${card.id}`;

export function cardTitle(entity: ScoreEntity, card: CardKind, criterion?: ScoreCriterion): string {
  if (card.type === 'field') return whereField(entity, card.key)?.label ?? card.key;
  if (card.type === 'relation') {
    const title = RELATION_CARD_TITLE[entity][card.relation];
    return (card.negated ? title?.none : title?.has) ?? card.relation;
  }
  return criterion?.label?.trim() || 'Custom condition';
}

/** The criteria in cards, each card in the order its first criterion appears. */
export function groupCriteria(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
  custom?: ReadonlySet<string>,
): { key: string; card: CardKind; title: string; criteria: ScoreCriterion[] }[] {
  const groups = new Map<string, { card: CardKind; criteria: ScoreCriterion[] }>();
  for (const criterion of criteria) {
    const card = cardOf(entity, criterion, custom);
    const key = cardKey(card);
    const group = groups.get(key) ?? { card, criteria: [] };
    group.criteria.push(criterion);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, group]) => ({
    key,
    card: group.card,
    title: cardTitle(entity, group.card, group.criteria[0]),
    criteria: group.criteria,
  }));
}

/** A stable id from a label: a-z, 0-9 and _, suffixed on a collision. */
export function newCriterionId(criteria: readonly ScoreCriterion[], base: string): string {
  const slug =
    base
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 32) || 'criterion';
  const taken = new Set(criteria.map((c) => c.id));
  if (!taken.has(slug)) return slug;
  let n = 2;
  while (taken.has(`${slug}_${n}`)) n += 1;
  return `${slug}_${n}`;
}

/** An empty rule on a field: its first operator, and the value that operator starts from. */
export function emptyRule(entity: ScoreEntity, key: string): WhereRule {
  const field = whereField(entity, key);
  const operator = field ? (operatorsFor(field)[0] ?? 'in') : 'in';
  if (operator === 'in' || operator === 'notIn') return { field: key, operator, value: [] };
  if (field?.kind === 'yes_no') return { field: key, operator, value: true };
  return { field: key, operator };
}

export type CardPick =
  | { type: 'field'; key: string }
  | { type: 'relation'; relation: WhereRelation; negated: boolean }
  | { type: 'custom' };

/** A fresh criterion for a rail pick or a card's "Add more values", at Important. */
export function newCriterionFor(entity: ScoreEntity, pick: CardPick): NewCriterion {
  if (pick.type === 'field') {
    return {
      impact: 'important',
      where: { combinator: 'and', rules: [emptyRule(entity, pick.key)] },
    };
  }
  // The field is picked first: the row opens on its field list, then its values.
  if (pick.type === 'relation') {
    return {
      impact: 'important',
      where: {
        combinator: 'and',
        ...(pick.negated ? { not: true } : {}),
        rules: [{ field: `${pick.relation}.`, operator: 'in', value: [] }],
      },
    };
  }
  return {
    impact: 'important',
    where: { combinator: 'and', rules: [{ field: '~', operator: 'in', value: [] }] },
  };
}

const CUSTOM_ID = /^custom(_\d+)?$/;

/** The editor for a score's stored criteria; a criterion shaped as a custom condition stays one while edited. */
export function editorFor(entity: ScoreEntity, criteria: ScoreCriterion[]): EditorState {
  // A picked Custom condition gets a "custom" id, so it loads back on its card whatever its shape.
  const custom = new Set(
    criteria
      .filter((c) => CUSTOM_ID.test(c.id) || cardOf(entity, c).type === 'custom')
      .map((c) => c.id),
  );
  return { criteria, dirty: false, custom };
}

export function addCriterion(
  state: EditorState,
  criterion: NewCriterion,
  opts: { custom?: boolean; saved?: readonly ScoreCriterion[] } = {},
): EditorState {
  const field = onlyRules(criterion.where)?.[0]?.field.replace(/\./g, '_');
  const base = opts.custom ? 'custom' : (criterion.label ?? (field || 'criterion'));
  // A removed saved criterion keeps its id reserved: a new row under it would read as that criterion being edited.
  const id = criterion.id ?? newCriterionId([...state.criteria, ...(opts.saved ?? [])], base);
  const custom = opts.custom ? new Set([...(state.custom ?? []), id]) : state.custom;
  return {
    ...state,
    dirty: true,
    criteria: [...state.criteria, { ...criterion, id }],
    ...(custom ? { custom } : {}),
  };
}

/** A cleared name is no name: the criterion reads by its rules. Every other label is kept. */
export function updateCriterion(
  state: EditorState,
  id: string,
  patch: Partial<ScoreCriterion>,
): EditorState {
  return {
    ...state,
    dirty: true,
    criteria: state.criteria.map((c) => {
      if (c.id !== id) return c;
      const next = { ...c, ...patch };
      if ('label' in patch && !patch.label?.trim()) {
        const { label: _label, ...unnamed } = next;
        return unnamed;
      }
      return next;
    }),
  };
}

export function removeCriterion(state: EditorState, id: string): EditorState {
  const custom = state.custom ? new Set([...state.custom].filter((c) => c !== id)) : undefined;
  return {
    ...state,
    dirty: true,
    criteria: state.criteria.filter((c) => c.id !== id),
    ...(custom ? { custom } : {}),
  };
}

const NO_VALUE = new Set(['null', 'notNull']);
const blank = (x: unknown) =>
  x === undefined || x === null || (typeof x === 'string' && x.trim() === '');

/** A condition still being filled in: no field yet, or no value where its operator needs one. */
export function ruleUnfinished(rule: WhereRule): boolean {
  if (isUnpicked(rule.field)) return true;
  if (NO_VALUE.has(rule.operator)) return false;
  const v = rule.value;
  if (blank(v)) return true;
  if (Array.isArray(v)) return v.length === 0 || v.some(blank);
  return (
    typeof v === 'object' && v !== null && 'unit' in v && blank((v as { amount?: unknown }).amount)
  );
}

/** A saved condition blanked, or a group emptied: as opposed to a new row with no field yet. */
const hasGap = (where: WhereGroup): boolean =>
  where.rules.length === 0 ||
  where.rules.some((node) =>
    isWhereGroup(node) ? hasGap(node) : ruleUnfinished(node) && !isUnpicked(node.field),
  );

const ruleKey = (rule: WhereRule) => JSON.stringify([rule.field, rule.operator, rule.value]);

/** The condition without its unfinished rows, and without groups they leave empty; null when nothing is left. */
export function finishedWhere(where: WhereGroup): WhereGroup | null {
  const rules = where.rules.flatMap((node): (WhereRule | WhereGroup)[] => {
    if (!isWhereGroup(node)) return ruleUnfinished(node) ? [] : [node];
    const kept = finishedWhere(node);
    return kept ? [kept] : [];
  });
  return rules.length ? { ...where, rules } : null;
}

/** What a save writes and the preview counts: finished conditions only; a criterion with none left is dropped. */
export function finishedCriteria(criteria: readonly ScoreCriterion[]): ScoreCriterion[] {
  return criteria.flatMap((c) => {
    const where = finishedWhere(c.where);
    return where ? [{ ...c, where }] : [];
  });
}

/** Whether saving would delete a saved criterion, or drop a rule it has because that row was left blank, which would widen it. */
function losesSavedRule(where: WhereGroup, stored: WhereGroup | undefined): boolean {
  if (!stored) return false;
  const finished = finishedWhere(where);
  if (!finished) return true;
  if (!hasGap(where)) return false;
  const kept = new Set(whereRules(finished).map(ruleKey));
  return whereRules(stored).some((rule) => !kept.has(ruleKey(rule)));
}

/** How many conditions are still being filled in; a save leaves them out. */
export function unfinishedCount(criteria: readonly ScoreCriterion[]): number {
  const count = (where: WhereGroup): number =>
    where.rules.length === 0
      ? 1
      : where.rules.reduce(
          (n, node) => n + (isWhereGroup(node) ? count(node) : ruleUnfinished(node) ? 1 : 0),
          0,
        );
  return criteria.reduce((n, c) => n + count(c.where), 0);
}

/** Why a save is refused, by id: a saved condition left blank, or the schema's reason for a finished one. */
export function criterionIssues(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
  saved: readonly ScoreCriterion[] = [],
): Map<string, string> {
  const schema = scoreCriterionSchema(entity);
  const issues = new Map<string, string>();
  for (const criterion of criteria) {
    if (losesSavedRule(criterion.where, saved.find((s) => s.id === criterion.id)?.where)) {
      issues.set(criterion.id, 'Finish the blank condition, or remove it.');
      continue;
    }
    const where = finishedWhere(criterion.where);
    if (!where) continue;
    const parsed = schema.safeParse({ ...criterion, where });
    if (parsed.success) continue;
    issues.set(criterion.id, parsed.error.issues[0]?.message ?? 'This criterion is incomplete.');
  }
  return issues;
}

/** The criteria the preview can run: every complete one. */
export function previewableCriteria(
  entity: ScoreEntity,
  criteria: readonly ScoreCriterion[],
  saved: readonly ScoreCriterion[] = [],
): ScoreCriterion[] {
  const issues = criterionIssues(entity, criteria, saved);
  return finishedCriteria(criteria).filter((criterion) => !issues.has(criterion.id));
}

/**
 * What a criterion asks for on the hover card: the chips are its first rule's values (any one counts),
 * and the note is every other rule, so "Tier 1" and "Developer" never read as two options.
 */
export function criterionChips(
  entity: ScoreEntity,
  criterion: ScoreCriterion,
  labels: DescribeLabels = {},
): { chips: string[]; note?: string } {
  const rules = onlyRules(criterion.where);
  const [first, ...rest] = rules ?? [];
  if (!rules || !first || criterion.where.combinator !== 'and' || criterion.where.not) {
    return { chips: [criterionLabel(entity, criterion)] };
  }
  const values = Array.isArray(first.value) && first.operator === 'in' ? first.value : null;
  const chips = values
    ? values.map((v) => whereValueText(entity, { ...first, value: [v] }, labels))
    : [ruleChip(entity, first, labels)];
  const note = rest.map((r) => describeRule(entity, r, labels)).join(' · ');
  return note ? { chips, note } : { chips };
}

const ruleChip = (entity: ScoreEntity, rule: WhereRule, labels: DescribeLabels) =>
  describeRule(entity, rule, labels).replace(`${whereFieldName(entity, rule.field)} `, '');

/** The hover card's name for what a criterion reads: its card's title, or a custom criterion's name. */
export function criterionTopic(entity: ScoreEntity, criterion: ScoreCriterion): string {
  const card = cardOf(entity, criterion);
  if (card.type === 'custom') return criterionLabel(entity, criterion);
  return cardTitle(entity, card, criterion);
}
